// src/components/CampaignOutboxTab.tsx
import React from 'react';
import {
  CampaignSummary,
  CampaignDetail,
  fetchCampaigns,
  fetchCampaignById,
  sendCampaignNow,
  CampaignStatus,
} from '../api/campaigns';

const STATUS_OPTIONS: { label: string; value: CampaignStatus | 'all' }[] = [
  { label: 'Drafts', value: 'draft' },
  { label: 'Scheduled', value: 'scheduled' },
  { label: 'Running', value: 'running' },
  { label: 'Sent / Finished', value: 'finished' },
  { label: 'All', value: 'all' },
];


const formatDate = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

const CampaignOutboxTab: React.FC = () => {
  const [statusFilter, setStatusFilter] = React.useState<
    CampaignStatus | 'all'
  >('draft');
  const [campaigns, setCampaigns] = React.useState<CampaignSummary[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [selectedCampaign, setSelectedCampaign] =
    React.useState<CampaignDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = React.useState(false);

  const [sendingId, setSendingId] = React.useState<number | null>(null);
  const [confirmSendId, setConfirmSendId] =
    React.useState<number | null>(null);

  // Load list whenever statusFilter changes
  React.useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchCampaigns(statusFilter);
        if (!cancelled) {
          setCampaigns(data);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err.message || 'Failed to load campaigns.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [statusFilter]);

  const handleView = async (id: number) => {
    setSelectedCampaign(null);
    setLoadingDetail(true);
    try {
      const detail = await fetchCampaignById(id);
      setSelectedCampaign(detail);
    } catch (err: any) {
      setError(err.message || 'Failed to load campaign details.');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleSendClick = (id: number) => {
    setConfirmSendId(id);
  };

  const handleConfirmSend = async () => {
    if (confirmSendId == null) return;
    const id = confirmSendId;
    setSendingId(id);
    setError(null);
    try {
      await sendCampaignNow(id);
      // Refresh list after sending
      const data = await fetchCampaigns(statusFilter);
      setCampaigns(data);
      setConfirmSendId(null);
    } catch (err: any) {
      setError(err.message || 'Failed to send campaign.');
    } finally {
      setSendingId(null);
    }
  };

  const handleCancelConfirm = () => {
    setConfirmSendId(null);
  };

  const canSend = (status: CampaignStatus) =>
    status === 'draft' || status === 'paused';

  return (
    <div className="space-y-4">
      {/* Filter row */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-gray-100">
          Campaign Outbox
        </h2>

        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-300">Status:</label>
          <select
            className="rounded-md border border-gray-600 bg-gray-800 px-2 py-1 text-sm text-gray-100"
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as CampaignStatus | 'all')
            }
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-500 bg-red-900/30 px-3 py-2 text-sm text-red-200">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-700 bg-gray-900/60">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-900/80">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-300">
                Name
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-300">
                Subject
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-300">
                Lists
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-300">
                Status
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-300">
                Created
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-300">
                Updated
              </th>
              <th className="px-3 py-2 text-right font-medium text-gray-300">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-3 py-4 text-center text-gray-400"
                >
                  Loading campaigns…
                </td>
              </tr>
            ) : campaigns.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-3 py-4 text-center text-gray-400"
                >
                  No campaigns found for this filter.
                </td>
              </tr>
            ) : (
              campaigns.map((c) => (
                <tr
                  key={c.id}
                  className="border-t border-gray-800 hover:bg-gray-800/60"
                >
                  <td className="px-3 py-2 text-gray-100">{c.name}</td>
                  <td className="px-3 py-2 text-gray-100">{c.subject}</td>
                  <td className="px-3 py-2 text-gray-200">
                    {c.lists && c.lists.length > 0
                      ? c.lists.map((l) => l.name).join(', ')
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-gray-200 capitalize">
                    {c.status}
                  </td>
                  <td className="px-3 py-2 text-gray-300">
                    {formatDate(c.createdAt)}
                  </td>
                  <td className="px-3 py-2 text-gray-300">
                    {formatDate(c.updatedAt)}
                  </td>
                  <td className="px-3 py-2 text-right space-x-2">
                    <button
                      className="rounded-md border border-gray-600 px-2 py-1 text-xs text-gray-100 hover:bg-gray-700"
                      onClick={() => handleView(c.id)}
                    >
                      View
                    </button>
                    {canSend(c.status) && (
                      <button
                        className="rounded-md bg-emerald-600 px-2 py-1 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
                        disabled={sendingId === c.id}
                        onClick={() => handleSendClick(c.id)}
                      >
                        {sendingId === c.id ? 'Sending…' : 'Send now'}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Detail panel */}
      {selectedCampaign && (
        <div className="space-y-2 rounded-lg border border-gray-700 bg-gray-900/80 p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-md font-semibold text-gray-100">
              {selectedCampaign.name}
            </h3>
            <button
              className="text-xs text-gray-400 hover:text-gray-200"
              onClick={() => setSelectedCampaign(null)}
            >
              Close
            </button>
          </div>

          {loadingDetail ? (
            <div className="text-sm text-gray-400">Loading details…</div>
          ) : (
            <>
              <p className="text-sm text-gray-300">
                <span className="font-medium">Subject:</span>{' '}
                {selectedCampaign.subject}
              </p>
              <p className="text-sm text-gray-300">
                <span className="font-medium">Status:</span>{' '}
                <span className="capitalize">
                  {selectedCampaign.status}
                </span>
              </p>
              <p className="text-sm text-gray-300">
                <span className="font-medium">Lists:</span>{' '}
                {selectedCampaign.lists && selectedCampaign.lists.length > 0
                  ? selectedCampaign.lists.map((l) => l.name).join(', ')
                  : '—'}
              </p>
              <p className="text-sm text-gray-300">
                <span className="font-medium">Created:</span>{' '}
                {formatDate(selectedCampaign.createdAt)}
              </p>
              <p className="text-sm text-gray-300">
                <span className="font-medium">Updated:</span>{' '}
                {formatDate(selectedCampaign.updatedAt)}
              </p>

              {selectedCampaign.previewUrl && (
                <p className="text-sm">
                  <a
                    href={selectedCampaign.previewUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-emerald-400 hover:underline"
                  >
                    Open Preview in Listmonk
                  </a>
                </p>
              )}
              {selectedCampaign.adminUrl && (
                <p className="text-sm">
                  <a
                    href={selectedCampaign.adminUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-emerald-400 hover:underline"
                  >
                    Open in Listmonk Admin
                  </a>
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* Confirm send modal */}
      {confirmSendId != null && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-sm rounded-lg border border-gray-700 bg-gray-900 p-4 shadow-lg">
            <h3 className="mb-2 text-md font-semibold text-gray-100">
              Send campaign?
            </h3>
            <p className="mb-4 text-sm text-gray-300">
              Are you sure you want to send this campaign now? This will
              queue it to all subscribers on the selected list(s).
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="rounded-md border border-gray-600 px-3 py-1 text-sm text-gray-100 hover:bg-gray-700"
                onClick={handleCancelConfirm}
                disabled={sendingId != null}
              >
                Cancel
              </button>
              <button
                className="rounded-md bg-emerald-600 px-3 py-1 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
                onClick={handleConfirmSend}
                disabled={sendingId != null}
              >
                {sendingId != null ? 'Sending…' : 'Yes, send now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignOutboxTab;
